import {
  ExecutionScenario,
  PacingResult,
  RaceSettings,
  SafePenaltyIntensity,
  Segment,
  TerrainType,
} from '../types'

const STANDARD_DURATION_THRESHOLDS = [
  { minMinutes: 120, factor: 1.05 },
  { minMinutes: 90, factor: 1.03 },
]

const STANDARD_TERRAIN_PENALTY: Record<TerrainType, number> = {
  Road: 1.0,
  SmoothTrail: 1.0,
  MixedTrail: 1.03,
  MudSand: 1.03,
  Rocky: 1.03, // Moderately technical
  Technical: 1.06, // Highly technical
  OfftrailSteep: 1.06,
}

const STANDARD_ELEVATION_DENSITY_THRESHOLD = 50
const STANDARD_ELEVATION_DENSITY_FACTOR = 1.03
const STANDARD_STOP_MULTIPLIER = 1.25

const SAFE_LATE_RACE_THRESHOLDS = [
  { pct: 0.8, factor: 1.08 },
  { pct: 0.6, factor: 1.05 },
]

const SAFE_NIGHT_PENALTY = {
  nonTechnical: 1.03,
  technical: 1.06,
}

const SAFE_HIGH_DPLUS_THRESHOLD = 80
const SAFE_HIGH_DPLUS_FACTOR = 1.06
const SAFE_STOP_MULTIPLIER = 1.6

const SAFE_INTENSITY_MODIFIERS: Record<SafePenaltyIntensity, number> = {
  low: 0.8,
  default: 1.0,
  high: 1.2,
}

function getElevationDensity(segment: Segment): number {
  if (segment.distance <= 0) return 0
  return segment.elevationGain / segment.distance
}

function isTechnical(terrain: TerrainType): boolean {
  return ['Rocky', 'Technical', 'OfftrailSteep'].includes(terrain)
}

export function applyScenario(
  optimalResults: PacingResult[],
  segments: Segment[],
  scenario: ExecutionScenario,
  settings: RaceSettings,
  totalDistance: number,
): PacingResult[] {
  // Optimal is the baseline (clean execution)
  if (scenario === 'optimal') {
    return optimalResults
  }

  const isSafe = scenario === 'safe'
  const safeIntensity =
    SAFE_INTENSITY_MODIFIERS[settings.safePenaltyIntensity || 'default']

  // Apply Athlete Risk Tolerance Bias (from profile)
  // Negative bias (finish-first) increases penalties slightly
  // Positive bias (performance) decreases them
  const riskBias = settings.athleteProfile.params.scenarioBias
  // e.g. bias 0.05 (performance) -> multiplier 0.95 (faster)
  // e.g. bias -0.05 (finish) -> multiplier 1.05 (slower)
  const biasMultiplier = 1.0 - riskBias

  let accumulatedMinutes = 0
  const startTime = new Date(settings.startTime)
  const sunriseHour = parseInt(settings.sunriseTime.split(':')[0])
  const sunsetHour = parseInt(settings.sunsetTime.split(':')[0])

  return optimalResults.map((res, index) => {
    const segment = segments.find((s) => s.id === res.segmentId)
    if (!segment) return res

    // If the user has set a custom pace override, skip all scenario adjustments
    // — the override is treated as an absolute, final value
    if (segment.paceOverride && segment.paceOverride > 0) {
      const stopTime = res.stopTime
      accumulatedMinutes += res.duration + stopTime
      const eta = new Date(startTime.getTime() + accumulatedMinutes * 60000)
      const etaHour = eta.getHours()
      const resultIsNight = etaHour < sunriseHour || etaHour >= sunsetHour

      return {
        ...res,
        stopTime,
        totalTime: res.duration + stopTime,
        accumulatedTime: accumulatedMinutes,
        eta,
        isNight: resultIsNight,
      }
    }

    let duration = res.duration

    // A. Standard Factors (Deterministic Friction)
    // 1. Duration Drag
    let durationFactor = 1.0
    for (const t of STANDARD_DURATION_THRESHOLDS) {
      if (res.duration > t.minMinutes) {
        durationFactor = t.factor
        break
      }
    }
    duration *= durationFactor

    // 2. Terrain Friction
    const terrainFactor =
      STANDARD_TERRAIN_PENALTY[segment.terrainType || 'SmoothTrail'] || 1.0
    duration *= terrainFactor

    // 3. Elevation Density Friction
    const density = getElevationDensity(segment)
    if (density > STANDARD_ELEVATION_DENSITY_THRESHOLD) {
      duration *= STANDARD_ELEVATION_DENSITY_FACTOR
    }

    // B. Safe Factors (Risk/Fatigue Layer)
    if (isSafe) {
      let distSoFar = 0
      for (let i = 0; i < index; i++) {
        distSoFar += segments[i].distance
      }
      const distPct = totalDistance > 0 ? distSoFar / totalDistance : 0

      // 1. Late Race Fatigue
      let fatigueFactor = 1.0
      for (const t of SAFE_LATE_RACE_THRESHOLDS) {
        if (distPct > t.pct) {
          const increase = (t.factor - 1.0) * safeIntensity
          fatigueFactor = 1.0 + increase
          break
        }
      }
      duration *= fatigueFactor

      // 2. Night Penalty
      const segmentStart = new Date(
        startTime.getTime() + accumulatedMinutes * 60000,
      )
      const hour = segmentStart.getHours()
      const isNight = hour < sunriseHour || hour >= sunsetHour

      if (isNight) {
        const nightFactor = isTechnical(segment.terrainType)
          ? SAFE_NIGHT_PENALTY.technical
          : SAFE_NIGHT_PENALTY.nonTechnical
        duration *= nightFactor
      }

      // 3. High D+ Penalty
      if (density > SAFE_HIGH_DPLUS_THRESHOLD) {
        duration *= SAFE_HIGH_DPLUS_FACTOR
      }
    }

    // Apply Risk Bias to the final duration
    duration *= biasMultiplier

    // Stop Times
    let stopTime = res.stopTime
    if (stopTime > 0) {
      const multiplier = isSafe
        ? SAFE_STOP_MULTIPLIER
        : STANDARD_STOP_MULTIPLIER
      stopTime *= multiplier
    }

    accumulatedMinutes += duration + stopTime
    const eta = new Date(startTime.getTime() + accumulatedMinutes * 60000)
    const etaHour = eta.getHours()
    const resultIsNight = etaHour < sunriseHour || etaHour >= sunsetHour
    const pace = segment.distance > 0 ? duration / segment.distance : 0

    return {
      ...res,
      duration,
      stopTime,
      totalTime: duration + stopTime,
      accumulatedTime: accumulatedMinutes,
      eta,
      isNight: resultIsNight,
      pace,
    }
  })
}
